import {
  TemplateID,
  HotTextTypes,
  MultipleChoiceTypes,
  NonAssessableTypes,
  TextEntryTypes,
} from '../shared/enums';
import {
  AnswerChoice,
  PopulationDNDGrid,
  PopulationDragAndDrop,
  PopulationHotText,
  PopulationInlineChoice,
  PopulationMultipleChoice,
  TargetedFeedback,
} from '../shared/interfaces';
import { PopulationTextEntry } from '../shared/interfaces/population-text-entry';
import { PopulationData } from '../shared/types/population-data';
import {
  DnDGridEvaluator,
  DnDGridMatchEvaluator,
  DynamicDnDEvaluator,
  HotTextEvaluator,
  InlineChoiceEvaluator,
  MultiChoiceEvaluator,
  TextEntryEvaluator,
} from './';
import { FillInTheBlankTileTextEvaluator } from './fill-in-the-blank-tile-evaluator';

const inlineChoiceTypes = [
  TemplateID.INLINE_CHOICE,
  TemplateID.STATIC_HORIZONTAL_SEQUENCING,
  TemplateID.STATIC_VERTICAL_SEQUENCING,
  TemplateID.FILL_IN_THE_BLANK_TILE_TEXT,
];
const dynamicDnDTypes = [
  TemplateID.DYNAMIC_HORIZONTAL_SEQUENCING,
  TemplateID.SORTING_BUCKETS,
  TemplateID.SORTING_BUCKETS_IMAGES,
];

export class EvaluatorFactory {
  static isCorrect(
    type: TemplateID,
    population: PopulationData,
    answers: (string | number)[][]
  ): boolean {
    if (type === TemplateID.FILL_IN_THE_BLANK_TILE_TEXT) {
      return FillInTheBlankTileTextEvaluator.isCorrect(
        population as PopulationInlineChoice,
        answers[0] as string[]
      );
    }
    if (MultipleChoiceTypes.includes(type)) {
      return MultiChoiceEvaluator.isCorrect(
        population as PopulationMultipleChoice,
        answers[0] as string[]
      );
    }

    if (inlineChoiceTypes.includes(type)) {
      return InlineChoiceEvaluator.isCorrect(
        population as PopulationInlineChoice,
        answers[0] as string[]
      );
    }

    if (TextEntryTypes.includes(type)) {
      return TextEntryEvaluator.isCorrect(
        population as PopulationTextEntry,
        answers
      );
    }

    if (dynamicDnDTypes.includes(type)) {
      return DynamicDnDEvaluator.isCorrect(
        population as PopulationDragAndDrop,
        answers,
        type === TemplateID.DYNAMIC_HORIZONTAL_SEQUENCING
      );
    }

    if (type === TemplateID.DND_GRID) {
      return DnDGridEvaluator.isCorrect(
        population as PopulationDNDGrid,
        answers
      );
    }

    if (type === TemplateID.DND_GRID_MATCH) {
      return DnDGridMatchEvaluator.isCorrect(
        population as PopulationDNDGrid,
        answers[0] as string[]
      );
    }

    if (HotTextTypes.includes(type)) {
      return HotTextEvaluator.isCorrect(
        population as PopulationHotText,
        answers as string[][]
      );
    }

    return false;
  }

  static getWrongAnswers(
    type: TemplateID,
    population: PopulationData,
    answers: (string | number)[][],
    disabledAnswers: (string | number)[][]
  ): (string | number)[][] {
    if (type === TemplateID.FILL_IN_THE_BLANK_TILE_TEXT) {
      return FillInTheBlankTileTextEvaluator.getWrongAnswers(
        population as PopulationInlineChoice,
        answers[0] as string[]
      ).map((wrong, index) => [...wrong, ...(disabledAnswers[index] || [])]);
    }
    if (MultipleChoiceTypes.includes(type)) {
      return [
        [
          ...disabledAnswers[0],
          ...MultiChoiceEvaluator.getWrongAnswers(
            population as PopulationMultipleChoice,
            answers[0] as string[]
          ),
        ],
      ];
    }

    if (inlineChoiceTypes.includes(type)) {
      return InlineChoiceEvaluator.getWrongAnswers(
        population as PopulationInlineChoice,
        answers[0] as string[]
      ).map((wrong, index) => [...wrong, ...(disabledAnswers[index] || [])]);
    }

    if (TextEntryTypes.includes(type)) {
      return TextEntryEvaluator.getWrongAnswers(
        population as PopulationTextEntry,
        answers
      ).map((wrong, index) => [...wrong, ...(disabledAnswers[index] || [])]);
    }

    if (dynamicDnDTypes.includes(type)) {
      return DynamicDnDEvaluator.getWrongAnswers(
        population as PopulationDragAndDrop,
        answers,
        type === TemplateID.DYNAMIC_HORIZONTAL_SEQUENCING
      ).map((wrong, index) => [...wrong, ...(disabledAnswers[index] || [])]);
    }

    if (type === TemplateID.DND_GRID) {
      return DnDGridEvaluator.getWrongAnswers(
        population as PopulationDNDGrid,
        answers
      ).map((wrong, index) => [...wrong, ...(disabledAnswers[index] || [])]);
    }

    if (HotTextTypes.includes(type)) {
      const currWrongAnswer = HotTextEvaluator.getWrongAnswers(
        population as PopulationHotText,
        answers as string[][]
      );
      let result = [];
      if (disabledAnswers.length === 1 && !disabledAnswers[0].length) {
        result[0] = [...currWrongAnswer[0]];
      } else result = [...currWrongAnswer, ...disabledAnswers];

      return result as (string | number)[][];
    }

    return [[]];
  }

  static getSelectedWrongAnswers(
    type: TemplateID,
    population: PopulationData,
    answers: (string | number)[][]
  ): (string | number)[][] {
    if (type === TemplateID.FILL_IN_THE_BLANK_TILE_TEXT) {
      return FillInTheBlankTileTextEvaluator.getWrongAnswers(
        population as PopulationInlineChoice,
        answers[0] as string[]
      ).map(wrong => [...wrong]);
    }

    if (MultipleChoiceTypes.includes(type)) {
      return [
        [
          ...MultiChoiceEvaluator.getWrongAnswers(
            population as PopulationMultipleChoice,
            answers[0] as string[]
          ),
        ],
      ];
    }

    if (inlineChoiceTypes.includes(type)) {
      return InlineChoiceEvaluator.getWrongAnswers(
        population as PopulationInlineChoice,
        answers[0] as string[]
      ).map(wrong => [...wrong]);
    }

    if (TextEntryTypes.includes(type)) {
      return TextEntryEvaluator.getWrongAnswers(
        population as PopulationTextEntry,
        answers
      ).map(wrong => [...wrong]);
    }

    if (dynamicDnDTypes.includes(type)) {
      return DynamicDnDEvaluator.getWrongAnswers(
        population as PopulationDragAndDrop,
        answers,
        type === TemplateID.DYNAMIC_HORIZONTAL_SEQUENCING
      ).map(wrong => [...wrong]);
    }

    if (type === TemplateID.DND_GRID) {
      return DnDGridEvaluator.getWrongAnswers(
        population as PopulationDNDGrid,
        answers
      ).map(wrong => [...wrong]);
    }

    if (HotTextTypes.includes(type)) {
      return HotTextEvaluator.getWrongAnswers(
        population as PopulationHotText,
        answers as string[][]
      ).map(wrong => [...wrong]);
    }

    return [[]];
  }

  static getSelectedWrongTargetedFeedback(
    type: TemplateID,
    selectedWrongAnswers: (number | string)[][],
    answerChoices: AnswerChoice[],
    population: PopulationData
  ): TargetedFeedback[] {
    if (
      MultipleChoiceTypes.includes(type) ||
      type === TemplateID.FILL_IN_THE_BLANK_TILE_TEXT
    ) {
      return MultiChoiceEvaluator.getSelectedWrongTargetedFeedback(
        selectedWrongAnswers,
        answerChoices
      );
    }

    if (inlineChoiceTypes.includes(type)) {
      return InlineChoiceEvaluator.getSelectedWrongTargetedFeedback(
        selectedWrongAnswers,
        answerChoices
      );
    }

    if (TextEntryTypes.includes(type)) {
      return TextEntryEvaluator.getSelectedWrongTargetedFeedback(
        selectedWrongAnswers,
        population as PopulationTextEntry
      );
    }

    if (dynamicDnDTypes.includes(type)) {
      return DynamicDnDEvaluator.getSelectedWrongTargetedFeedback(
        selectedWrongAnswers,
        answerChoices
      );
    }

    if (type === TemplateID.DND_GRID) {
      return DnDGridEvaluator.getSelectedWrongTargetedFeedback(
        selectedWrongAnswers,
        answerChoices
      );
    }

    return [];
  }

  static removeWrongAnswers(
    type: TemplateID,
    wrong: (string | number)[][],
    answers: (string | number)[][]
  ): (string | number)[][] {
    if (MultipleChoiceTypes.includes(type)) {
      return [
        MultiChoiceEvaluator.removeWrongAnswers(
          wrong[0] as string[],
          answers[0] as string[]
        ),
      ];
    }

    if (inlineChoiceTypes.includes(type)) {
      return [
        InlineChoiceEvaluator.removeWrongAnswers(
          wrong as string[][],
          answers[0] as string[]
        ),
      ];
    }

    if (TextEntryTypes.includes(type)) {
      return TextEntryEvaluator.removeWrongAnswers(wrong, answers);
    }

    if (
      dynamicDnDTypes.includes(type) &&
      type !== TemplateID.DYNAMIC_HORIZONTAL_SEQUENCING
    ) {
      return DynamicDnDEvaluator.removeWrongAnswers(wrong, answers);
    }

    if (HotTextTypes.includes(type)) {
      return HotTextEvaluator.removeWrongAnswers(wrong, answers as string[][]);
    }

    return [[]];
  }

  static isNotAssessable(type: TemplateID): boolean {
    return NonAssessableTypes.includes(type);
  }
}
